import { useState, type ChangeEvent, type KeyboardEvent } from 'react';

import type { PalletUnitsField } from './types';

/**
 * The units field of a line on a pallet, like the legacy widget's quantity field:
 * the typed value is a draft until blur or Enter, then capped at `max` and saved
 * if it changed; an invalid value is dropped. Enter does not submit the
 * consignment form around the pallet view. With no draft the field shows `units`,
 * so a save — or its rollback — shows up on its own.
 */
export function usePalletUnitsInput({ units, max, onCommit }: Pick<PalletUnitsField, 'units' | 'max' | 'onCommit'>) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    const next = Math.min(parseInt(draft, 10), max);
    if (Number.isNaN(next) || next < 0 || next === units) return;
    void onCommit(next);
  };

  return {
    value: draft ?? String(units),
    onChange: (event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value),
    onBlur: commit,
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      commit();
    }
  };
}
